// src/ConsumerApp/Screens/Addresses/DeliveryAddressModal.js

import React, { useState, useMemo, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import AddressCard from './components/AddressCard';

// Real SVGs
import X from '../../../assets/icons/X.svg';
import Search from '../../../assets/icons/MagnifyingGlass.svg';
import TargetLocation from '../../../assets/icons/MapPin.svg';
import Plus from '../../../assets/icons/Plus.svg';
import CaretRight from '../../../assets/icons/CaretRight.svg';

// API Services & Hooks
import { LocationService } from '../../api/location/location.service';
import {
  useGetDeliveryAddresses,
  useDeleteDeliveryAddress,
  useSelectDeliveryAddress,
} from '../../api/addresses/deliveryAddress.hooks';
import { useGetDefaultDeliveryAddress } from '../../api/projects/project.hooks';

export default function DeliveryAddressModal({ isOpen, onClose, onAddNewAddress }) {
  const queryClient = useQueryClient();

  const customer = useMemo(() => {
    try {
      const stored = sessionStorage.getItem('userData') || localStorage.getItem('userData');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }, []);

  const customerId = customer?._id;

  // Search & Suggestion States[cite: 14]
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [activeMenuId, setActiveMenuId] = useState(null);

  const debounceTimer = useRef(null);
  const sessionToken = useMemo(
    () => `${Date.now()}_${Math.random().toString(36).slice(2)}`,
    [isOpen]
  );

  // Queries
  const { data: rawAddresses, isLoading: isAddressesLoading } = useGetDeliveryAddresses(customerId);
  const { data: defaultAddressData } = useGetDefaultDeliveryAddress(customerId);
  const selectAddressMutation = useSelectDeliveryAddress();
  const deleteAddressMutation = useDeleteDeliveryAddress();

  const addresses = useMemo(() => {
    const list = rawAddresses?.data || rawAddresses || [];
    return Array.isArray(list) ? list : [];
  }, [rawAddresses]);

  const defaultId =
    defaultAddressData?.delivery_address_id ||
    defaultAddressData?._id ||
    defaultAddressData?.id ||
    defaultAddressData?.data?.delivery_address_id ||
    defaultAddressData?.data?._id;

  // Debounced Autocomplete Search[cite: 14]
  const handleSearchChange = useCallback(
    (e) => {
      const text = e.target.value;
      setSearchQuery(text);

      if (debounceTimer.current) clearTimeout(debounceTimer.current);

      if (text.trim().length < 2) {
        setSearchResults([]);
        setIsSearching(false);
        return;
      }

      setIsSearching(true);
      debounceTimer.current = setTimeout(async () => {
        try {
          const results = await LocationService.searchPlaces(text.trim(), sessionToken);
          setSearchResults(Array.isArray(results) ? results : []);
        } catch {
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      }, 350);
    },
    [sessionToken]
  );

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
  };

  // Local Filter for Saved Addresses[cite: 14]
  const filteredSavedAddresses = useMemo(() => {
    if (!searchQuery.trim()) return addresses;
    const q = searchQuery.toLowerCase();
    return addresses.filter(
      (a) =>
        (a.project_name && a.project_name.toLowerCase().includes(q)) ||
        (a.area_sector_locality && a.area_sector_locality.toLowerCase().includes(q)) ||
        (a.fullAddress && a.fullAddress.toLowerCase().includes(q)) ||
        (a.city && a.city.toLowerCase().includes(q))
    );
  }, [addresses, searchQuery]);

  const handleSelect = async (addressId) => {
    if (!customerId) return;
    try {
      await selectAddressMutation.mutateAsync({
        customer_id: customerId,
        delivery_address_id: addressId,
      });
      queryClient.invalidateQueries({ queryKey: ['defaultDeliveryAddress'] });
      queryClient.invalidateQueries({ queryKey: ['deliveryAddresses'] });
      queryClient.invalidateQueries({ queryKey: ['bucketDetails'] });
      onClose();
    } catch {
      alert('Could not set the delivery address.');
    }
  };

  const handleDelete = (item) => {
    if (window.confirm(`Delete address "${item.area_sector_locality || item.project_name || 'Selected'}"?`)) {
      deleteAddressMutation.mutate(item._id);
    }
  };

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser.');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (onAddNewAddress) {
          onAddNewAddress({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        }
      },
      () => {
        alert('Please allow location permissions to use your current location.');
      }
    );
  };

  const handleSelectPlaceResult = async (place) => {
    try {
      const details = await LocationService.getPlaceDetails(place.place_id, sessionToken);
      onClose();
      if (onAddNewAddress) {
        onAddNewAddress({
          placeId: place.place_id,
          description: place.description,
          details,
        });
      }
    } catch {
      onClose();
      if (onAddNewAddress) {
        onAddNewAddress({ placeId: place.place_id, description: place.description });
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div style={styles.backdrop} onClick={onClose}>
      <style>{`
        @media (max-width: 600px) {
          .zepto-address-modal {
            width: 100vw !important;
            height: 100vh !important;
            max-height: 100vh !important;
            border-radius: 0 !important;
          }
        }
      `}</style>

      <div
        className="zepto-address-modal"
        style={styles.modalBox}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={styles.header}>
          <h3 style={styles.headerTitle}>Select Location</h3>
          <button style={styles.closeBtn} onClick={onClose} type="button">
            <img src={X} alt="close" style={{ width: 16, height: 16 }} />
          </button>
        </div>

        {/* Search Field */}
        <div style={styles.searchSection}>
          <div style={styles.searchBar}>
            <img src={Search} alt="search" style={{ width: 18, height: 18, opacity: 0.6 }} />
            <input
              type="text"
              placeholder="Search area, apartment or site address..."
              value={searchQuery}
              onChange={handleSearchChange}
              style={styles.searchInput}
            />
            {searchQuery && (
              <button style={styles.clearBtn} onClick={clearSearch} type="button">
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Quick Action Rows[cite: 14] */}
        {!searchQuery && (
          <div style={styles.quickActions}>
            <div style={styles.actionRow} onClick={handleUseCurrentLocation}>
              <div style={styles.actionIconCircle}>
                <img src={TargetLocation} alt="gps" style={{ width: 18, height: 18 }} />
              </div>
              <div style={{ flex: 1 }}>
                <span style={styles.actionTitleRed}>Use my Current Location</span>
                <span style={styles.actionSubtitle}>Using GPS</span>
              </div>
            </div>

            <div style={styles.actionDivider} />

            <div
              style={styles.actionRow}
              onClick={() => {
                onClose();
                if (onAddNewAddress) onAddNewAddress();
              }}
            >
              <div style={styles.actionIconCircle}>
                <img src={Plus} alt="add" style={{ width: 18, height: 18 }} />
              </div>
              <div style={{ flex: 1 }}>
                <span style={styles.actionTitleRed}>Add New Address</span>
              </div>
              <img src={CaretRight} alt="arrow" style={{ width: 14, height: 14, opacity: 0.4 }} />
            </div>
          </div>
        )}

        {/* Scrollable Results Area */}
        <div style={styles.savedSection}>
          {/* Autocomplete Predictions from Backend[cite: 14] */}
          {searchQuery && (
            <>
              <span style={styles.sectionHeading}>Location Suggestions</span>
              {isSearching ? (
                <div style={styles.loadingText}>Searching locations...</div>
              ) : searchResults.length > 0 ? (
                <div style={styles.placesList}>
                  {searchResults.map((place) => (
                    <div
                      key={place.place_id}
                      style={styles.placeRow}
                      onClick={() => handleSelectPlaceResult(place)}
                    >
                      <img
                        src={TargetLocation}
                        alt="pin"
                        style={{ width: 18, height: 18, marginTop: 2, opacity: 0.7 }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <span style={styles.placeMainText}>
                          {place.structured_formatting?.main_text || place.description}
                        </span>
                        {place.structured_formatting?.secondary_text && (
                          <span style={styles.placeSubText}>
                            {place.structured_formatting.secondary_text}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
            </>
          )}

          {/* Saved Addresses Section[cite: 14] */}
          <span style={{ ...styles.sectionHeading, marginTop: searchQuery ? 16 : 0 }}>
            {searchQuery ? 'Saved Addresses Matching Search' : 'Saved Addresses'}
          </span>

          <div style={styles.addressesList}>
            {isAddressesLoading ? (
              <div style={styles.loadingText}>Fetching saved addresses...</div>
            ) : filteredSavedAddresses.length === 0 ? (
              <div style={styles.emptyText}>
                {searchQuery
                  ? `No saved addresses matching "${searchQuery}"`
                  : 'No saved delivery addresses found.'}
              </div>
            ) : (
              filteredSavedAddresses.map((addr) => (
                <AddressCard
                  key={addr._id}
                  data={addr}
                  isSelected={defaultId === addr._id}
                  onSelect={() => handleSelect(addr._id)}
                  isMenuOpen={activeMenuId === addr._id}
                  onToggleMenu={() => setActiveMenuId(activeMenuId === addr._id ? null : addr._id)}
                  onEdit={() => {
                    onClose();
                    if (onAddNewAddress) onAddNewAddress({ editId: addr._id });
                  }}
                  onDelete={() => handleDelete(addr)}
                />
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 10000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backdropFilter: 'blur(3px)',
  },
  modalBox: {
    backgroundColor: '#FAF9F6',
    width: '100%',
    maxWidth: '500px',
    maxHeight: '85vh',
    borderRadius: '16px',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
    overflow: 'hidden',
  },
  header: {
    padding: '16px 20px',
    backgroundColor: '#FFFFFF',
    borderBottom: '1px solid #E2E8F0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerTitle: {
    margin: 0,
    fontSize: '17px',
    fontWeight: '700',
    color: '#2B2A29',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    padding: '6px',
    borderRadius: '6px',
  },
  searchSection: {
    padding: '14px 20px 8px 20px',
    backgroundColor: '#FFFFFF',
  },
  searchBar: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#F4F5F7',
    borderRadius: '10px',
    padding: '10px 14px',
    gap: '10px',
  },
  searchInput: {
    flex: 1,
    border: 'none',
    outline: 'none',
    backgroundColor: 'transparent',
    fontSize: '13px',
    color: '#2B2A29',
  },
  clearBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#707B8C',
    fontSize: '12px',
  },
  quickActions: {
    margin: '12px 20px 0 20px',
    backgroundColor: '#FFFFFF',
    borderRadius: '12px',
    border: '1px solid #E2E8F0',
    overflow: 'hidden',
  },
  actionRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    gap: '12px',
    cursor: 'pointer',
  },
  actionIconCircle: {
    width: '32px',
    height: '32px',
    borderRadius: '16px',
    backgroundColor: '#FFF1F2',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  actionTitleRed: {
    fontSize: '13px',
    fontWeight: '700',
    color: '#E9001E',
    display: 'block',
  },
  actionSubtitle: {
    fontSize: '11px',
    color: '#707B8C',
    display: 'block',
  },
  actionDivider: {
    height: '1px',
    backgroundColor: '#F1F5F9',
  },
  savedSection: {
    padding: '16px 20px 24px 20px',
    overflowY: 'auto',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  sectionHeading: {
    fontSize: '12px',
    fontWeight: '700',
    color: '#707B8C',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    marginBottom: '10px',
    display: 'block',
  },
  placesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginBottom: '16px',
  },
  placeRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    padding: '10px 14px',
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    border: '1px solid #E2E8F0',
    cursor: 'pointer',
  },
  placeMainText: {
    display: 'block',
    fontSize: '13px',
    fontWeight: '600',
    color: '#2B2A29',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  placeSubText: {
    display: 'block',
    fontSize: '11px',
    color: '#707B8C',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  addressesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  loadingText: {
    padding: '24px',
    textAlign: 'center',
    fontSize: '13px',
    color: '#707B8C',
  },
  emptyText: {
    padding: '24px',
    textAlign: 'center',
    fontSize: '13px',
    color: '#707B8C',
    backgroundColor: '#FFFFFF',
    borderRadius: '10px',
    border: '1px dashed #CBD5E1',
  },
};